/** 与 openapi/studio-api-mvp-v1.yaml 对齐 */

export interface ApiFieldError {
  field: string;
  message: string;
}

export interface ApiValidationErrors {
  errors: ApiFieldError[];
}

export interface ApiResponse<T> {
  success: boolean;
  code: string;
  message: string;
  data: T;
}

export class StudioApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly httpStatus?: number,
    readonly fieldErrors?: ApiFieldError[],
    readonly billingReason?: BillingGateReason,
    readonly accessReason?: AppAccessReason,
  ) {
    super(message);
    this.name = "StudioApiError";
  }
}

export type BillingGateReason =
  | "subscription_required"
  | "upgrade_to_pro_required";

export type AppAccessReason = "app_uninstalled" | "app_suspended";

export type AppAccountStatus = "active" | "uninstalled" | "suspended";

export type ExportJobStatus =
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELED";

export type NotifyStatus = "NOT_SENT" | "SENT" | "FAILED";

export interface ExportJobItem {
  invoice_id: string;
  item_status: "SUCCESS" | "FAILED" | "SKIPPED" | "PENDING";
  customer_id: string | null;
  customer_name: string | null;
  invoice_number: string | null;
  invoice_created_at: string | null;
  amount_due: number | null;
  currency: string | null;
  /** Non-empty for FAILED / SKIPPED only */
  remark: string | null;
}

export interface ExportJobDetail {
  job_id: string;
  status: ExportJobStatus;
  source?: string;
  invoice_count: number;
  invoice_success_count: number;
  invoice_failed_count: number;
  progress_percent: number;
  notify_status: NotifyStatus;
  created_at: string;
  finished_at: string | null;
  /** started_at to finished_at (or now when RUNNING); null when not started */
  duration_ms?: number | null;
  error_code: string | null;
  error_message: string | null;
  document_type?: string;
  display_title?: string | null;
  zip_size_bytes?: number | null;
  notify_email?: string | null;
  filter_summary?: string | null;
  /** Raw filter_snapshot_json from DB (string or parsed object) */
  filter_snapshot_json?: string | Record<string, unknown> | null;
  items?: ExportJobItem[];
  files?: ExportJobFile[];
}

export interface ExportJobFile {
  file_id: number;
  volume_index: number;
  volume_count?: number | null;
  file_name: string;
  file_size_bytes?: number | null;
}

export interface ExportJobListResponse {
  items: ExportJobDetail[];
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface ExportJobSummaryResponse {
  total: number;
  running: number;
  ready: number;
  failed: number;
}

export interface ExportPreviewCountResponse {
  count: number;
  capped: boolean;
}

export interface CustomerRow {
  id: string;
  name: string | null;
  email: string | null;
}

export interface CustomerListResponse {
  items: CustomerRow[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface InvoiceRow {
  id: string;
  customer_id: string;
  customer_name?: string | null;
  customer_email?: string | null;
  status: string;
  currency: string;
  amount_due: number;
  created: number;
  exportable: boolean;
  invoice_number?: string | null;
  /** Stripe invoice_pdf URL from search; row download reuses this to skip retrieve. */
  invoice_pdf_url?: string | null;
  due_date?: number | null;
  hosted_invoice_url?: string | null;
}

export interface InvoiceSearchRequest {
  customer_ids?: string[];
  statuses?: string[];
  page: number;
  page_size: number;
  after_invoice_id?: string;
  created_from?: number;
  created_to?: number;
  due_from?: number;
  due_to?: number;
  amount_due_min?: number;
  amount_due_max?: number;
}

export interface InvoiceSearchResponse {
  items: InvoiceRow[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface PayLinkRow {
  invoice_id: string;
  invoice_number: string;
  customer_id: string;
  customer_name: string;
  customer_email?: string | null;
  amount: number;
  currency: string;
  status: string;
  created: number;
  due_date: number | null;
  hosted_url: string;
}

export interface PayLinkSearchResponse {
  items: PayLinkRow[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface CreditNoteRow {
  id: string;
  number: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  customer_id: string;
  customer_name: string | null;
  amount: number;
  currency: string;
  status: string;
  created: number;
  exportable: boolean;
  pdf_url?: string | null;
}

export interface CreditNoteSearchRequest {
  customer_ids?: string[];
  statuses?: string[];
  page: number;
  page_size: number;
  after_credit_note_id?: string;
  created_from?: number;
  created_to?: number;
  amount_min?: number;
  amount_max?: number;
}

export interface CreditNoteSearchResponse {
  items: CreditNoteRow[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface SkippedInvoice {
  invoice_id: string;
  reason: string;
  message: string;
}

export interface CreateExportJobFromSelectionRequest {
  resource_ids: string[];
  /** invoice_pdf | receipt | credit_note */
  document_type: string;
  notify_email_enabled?: boolean;
  notify_email_to?: string;
}

export interface DashboardStats {
  all_docs: number;
  pdfs: number;
  receipts: number;
  pay_links: number;
  credit_notes: number;
  invoice_count: number;
  total_exports: number;
  exports_in_progress: number;
  pdfs_exported: number;
  sample_limited: boolean;
}

export interface BillingStatus {
  vip_level: string;
  trial_ends_at: string;
  trial_days_left: number;
  trial_active: boolean;
  subscribed: boolean;
  can_manage_billing?: boolean;
  cancel_at_period_end?: boolean;
  can_export: boolean;
  exports_used_this_month: number;
  max_exports_per_month: number;
  max_items_per_job: number;
  can_inline_pdf: boolean;
  can_paginate: boolean;
  can_selection_export: boolean;
  can_bulk_export: boolean;
  can_pay_links_csv: boolean;
  can_redownload_history: boolean;
}

export interface BillingCheckoutSessionRequest {
  vip_level: string;
  return_origin?: string;
}

export interface BillingCheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface BillingPortalSessionRequest {
  intent?: "manage" | "upgrade_pro" | "cancel";
  return_origin?: string;
}

export interface BillingPortalSessionResponse {
  portal_url: string;
}

export interface CreateExportJobFromFiltersRequest {
  client_source?: "drawer" | "web";
  customer_ids?: string[];
  statuses?: string[];
  created_from?: number;
  created_to?: number;
  due_from?: number;
  due_to?: number;
  amount_due_min?: number;
  amount_due_max?: number;
  document_type?: "invoice_pdf" | "receipt" | "credit_note";
  notify_email_enabled?: boolean;
  notify_email_to?: string;
}

export interface CreateExportJobResponse {
  job_id: string;
}
